/**
 * @fileoverview PHARMAKON — Auth Store
 * @module stores/authStore
 */

import { create } from 'zustand';
import api from '../services/api.client.js';
import { isBetaMode, exitBeta } from '../mock/beta-mode.js';
// X XWP-18 — logout closes the caissier login session: the Caisse libre windows release their holds.
import { fermerSessionCaisse } from './caisseFenetresStore.js';
// X XWP-72 — a user picked by PIN on a team workstation (lib/session-equipe.js owns its rules).
import {
  brancherPurgeUtilisateur,
  enregistrerSelection,
  estSelectionEquipe,
  oublierEtatNavigateur,
  retournerAuChoix,
  viderFenetresCaisse,
} from '../lib/session-equipe.js';
import { equipeApi } from '../services/session-equipe.api.js';
import { MOTIFS_RETOUR_CHOIX } from '../config/session-equipe.config.js';

const TOKEN_KEY   = 'pharmakon_token';
const REFRESH_KEY = 'pharmakon_refresh';
const USER_KEY    = 'pharmakon_user';

/** Forget the session's own keys in this browser (token, refresh token, user). */
function oublierCles() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

export const useAuthStore = create((set, get) => ({
  user:    JSON.parse(localStorage.getItem(USER_KEY) || 'null'),
  loading: false,
  error:   null,

  isAuthenticated: () => !!get().user,

  hasRole: (...roles) => roles.includes(get().user?.role),

  canSee: (field) => {
    const HIDDEN = ['prix_achat_ht','prix_achat_ttc','marge_pct','montant_ht','montant_tva','montant_ttc'];
    if (get().user?.role === 'CAISSIER' && HIDDEN.includes(field)) return false;
    return true;
  },

  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const res = await api.post('/auth/login', { email, password });
      const { access_token, refresh_token, user } = res.data;
      localStorage.setItem(TOKEN_KEY,   access_token);
      localStorage.setItem(REFRESH_KEY, refresh_token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      set({ user, loading: false });
      return user;
    } catch (e) {
      const msg = e.message ?? 'Identifiants incorrects';
      set({ error: msg, loading: false });
      throw new Error(msg);
    }
  },

  /**
   * X XWP-72 — the PIN switch answered (`POST /auth/equipe/selection`): the user's OWN JWT, no refresh
   * token (a picked user is renewed by `/auth/equipe/selection/prolonger`, never `/auth/refresh`).
   */
  ouvrirSelectionEquipe: (r) => {
    localStorage.setItem(TOKEN_KEY, r.access_token);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.setItem(USER_KEY, JSON.stringify(r.user));
    enregistrerSelection(r);
    set({ user: r.user, error: null });
    return r.user;
  },

  /** X XWP-72 — the picked user's prolonged JWT (lib/session-equipe.js calls it while they are active). */
  enregistrerJetonEquipe: (token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
  },

  /**
   * X XWP-72 — « Changer d'utilisateur »: the pending caisse changes are committed with the user's own
   * token, the selection ends server-side (`POST /auth/equipe/changer`), then the user's browser state
   * is forgotten and the picker reloads. The workstation stays open; the server drafts persist (the
   * caisse login session is NOT closed — the same user picked again finds their windows).
   */
  changerUtilisateur: async () => {
    if (!estSelectionEquipe()) return get().logout();
    await viderFenetresCaisse();
    try {
      await equipeApi.changer();
    } catch {
      /* already ended server-side (revoked, idle): the picker is the answer anyway */
    }
    retournerAuChoix(MOTIFS_RETOUR_CHOIX.CHANGEMENT);
  },

  logout: async () => {
    // BETA fast-path: no network call, just wipe the in-memory mock store.
    if (isBetaMode()) {
      exitBeta();
      set({ user: null });
      return;
    }
    // X XWP-72 — on a team workstation « se déconnecter » hands the poste back to the picker.
    if (estSelectionEquipe()) return get().changerUtilisateur();
    try {
      // before the token goes: the session close is an authenticated call (XD-39)
      await fermerSessionCaisse(get().user?.id);
      const rt = localStorage.getItem(REFRESH_KEY);
      if (rt) await api.post('/auth/logout', { refresh_token: rt }).catch(() => {});
    } finally {
      const uid = get().user?.id ?? null;
      oublierCles();
      // Drop every cached caisse snapshot (key shape: pharmakon_caisse_current:<uid>) and the tab
      // mirror of the caisse windows. Keeps logout idempotent — next login won't accidentally hydrate
      // a previous user's session (X XWP-72: the same purge as « Changer d'utilisateur »).
      oublierEtatNavigateur(uid);
      set({ user: null });
    }
  },

  refreshUser: async () => {
    try {
      const res = await api.get('/auth/me');
      const user = res.data;
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      set({ user });
    } catch { get().logout(); }
  },
}));

// X XWP-72 — the idle return / an ended picked session forget the user's own keys too.
brancherPurgeUtilisateur(() => {
  oublierCles();
  useAuthStore.setState({ user: null });
});
